import {
  Box,
  CircularProgress,
  Paper,
  TextField,
  TableBody,
  TableRow,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
  Divider,
  DialogActions,
} from "@material-ui/core";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Controls from "../../../components/controls/Controls";
import Button from "../../../components/controls/button";
import { baseUrl } from "../../../config/const";
import useTable from "../../../components/useTable";
import OperDialog from "./components/Dialog";
import { SuggestionModalContent } from "./OperStokBySKU";
import { v4 as uuidv4 } from "uuid";

const MOMENTUM_API_URL = "https://notoputrooffice.com/api/analytics/momentum";

const categories = [
  { id: "ALL", name: "All" },
  { id: "Anak", name: "Anak" },
  { id: "Pria", name: "Pria" },
  { id: "Wanita", name: "Wanita" },
  { id: "Pollux", name: "Pollux" },
  { id: "Sport", name: "Sports" },
  { id: "Harian", name: "Harian" },
  // { id: "Aksesoris", name: "Aksesoris" },
];

const segmentByCategory = {
  Pria: "P",
  Wanita: "W",
  Anak: "A",
  Sport: "S",
  Harian: "H",
  Pollux: "WPO",
};

const normalizeRows = (payload) => {
  const rawRows = Array.isArray(payload)
    ? payload
    : payload?.results && Array.isArray(payload.results)
    ? payload.results
    : payload?.data && Array.isArray(payload.data)
    ? payload.data
    : payload?.data?.results && Array.isArray(payload.data.results)
    ? payload.data.results
    : payload?.rows && Array.isArray(payload.rows)
    ? payload.rows
    : [];

  return rawRows.map((item, index) => ({
    id: item.id || `${item.SKU || item.sku || item.kode || "SKU"}-${index}`,
    SKU: item.SKU || item.sku || item.kode || "-",
    global_stock:
      item.global_stock ??
      item.globalStock ??
      item.current_stock ??
      item.total_stock,
    selected_store_stock:
      item.selected_store_stock ?? item.selectedStoreStock ?? item.store_stock,
    first_seen_date:
      item.first_seen_date ?? item.firstSeenDate ?? item.created_at,
    SKU_age_days:
      item.SKU_age_days ?? item.sku_age_days ?? item.skuAgeDays ?? item.age,
    momentum_ratio: item.momentum_ratio ?? item.momentumRatio,
    koleksi: item.koleksi,
    status: item.status,
    recommendation_action:
      item.recommendation_action ??
      item.recommendationAction ??
      item.recommended_action,
    urgency: item.urgency ?? item.recommendation_urgency,
    store_distribution: item.store_distribution || [],
    raw: item,
  }));
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString("en-US") : value;
};

const formatRatio = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : value;
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(parsed.getDate()).padStart(2, "0")}`;
};

const isOperRecommendation = (action) => {
  const normalized = String(action || "").toLowerCase();
  return (
    normalized.includes("redistribution") ||
    normalized.includes("stock_allocation")
  );
};

const normalizeStatus = (value) => String(value || "").toLowerCase();
const isInboundStatus = (status) =>
  ["healthy", "rising", "slowing"].includes(normalizeStatus(status));
const isOutboundStatus = (status) =>
  ["collapsing", "dead", "national_dead"].includes(normalizeStatus(status));

const formatDatePayload = (date) => {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const renderRawValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const MomentumAnalytics = () => {
  const theme = useTheme();
  const matchSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [toko, setToko] = useState("PA2M");
  const [kategori, setKategori] = useState("Anak");
  const [skuKeywordInput, setSkuKeywordInput] = useState("");
  const [skuKeyword, setSkuKeyword] = useState("");
  const [listToko, setListToko] = useState([]);
  const [momentumRows, setMomentumRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const skuDebounceRef = useRef(null);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);

  const [operDialogOpen, setOperDialogOpen] = useState(false);
  const [selectedOperRow, setSelectedOperRow] = useState(null);
  const [operMode, setOperMode] = useState("inbound");

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    location: "",
  });

  const showSelectedStoreStock = toko !== "ALL";

  const filteredRows = useMemo(() => {
    const keyword = skuKeyword.trim().toLowerCase();

    if (!keyword) {
      return momentumRows;
    }

    return momentumRows.filter((row) =>
      String(row.SKU || "")
        .toLowerCase()
        .includes(keyword)
    );
  }, [momentumRows, skuKeyword]);

  const headCells = useMemo(() => {
    const columns = [
      { id: "SKU", label: "SKU", disableSorting: true },
      { id: "global_stock", label: "global_stock", disableSorting: true },
      {
        id: "first_seen_date",
        label: "first_seen_date",
        disableSorting: true,
      },
      { id: "SKU_age_days", label: "SKU_age_days", disableSorting: true },
      {
        id: "momentum_ratio",
        label: "momentum_ratio",
        disableSorting: true,
      },
      { id: "koleksi", label: "koleksi", disableSorting: true },
      { id: "status", label: "status", disableSorting: true },
      {
        id: "recommendation_action",
        label: "recommendation_action",
        disableSorting: true,
      },
      { id: "urgency", label: "urgency", disableSorting: true },
      { id: "actions", label: "actions", disableSorting: true },
    ];

    if (showSelectedStoreStock) {
      columns.splice(2, 0, {
        id: "selected_store_stock",
        label: "selected_store_stock",
        disableSorting: true,
      });
    }

    return columns;
  }, [showSelectedStoreStock]);

  const {
    TblHead,
    TblContainer,
    TblPagination,
    styledTableCell: TableCell,
    recordsAfterPagingAndSorting,
  } = useTable(filteredRows, headCells, {
    fn: (items) => items,
  });

  const getListToko = async () => {
    try {
      const {
        data: { data },
      } = await axios.get(`${baseUrl}/getAllToko`);

      const mappedStores = (data || []).map((store) => ({
        id: store.name,
        name: store.name,
      }));

      setListToko([{ id: "ALL", name: "ALL" }, ...mappedStores]);
    } catch (err) {
      console.error(err);
      setListToko([{ id: "ALL", name: "ALL" }]);
    }
  };

  const getMomentumData = async () => {
    setIsLoading(true);

    try {
      const { data } = await axios.get(MOMENTUM_API_URL, {
        params: {
          toko,
          kategori: kategori === "ALL" ? "" : kategori,
        },
      });

      setMomentumRows(normalizeRows(data));
    } catch (err) {
      console.error(err);
      setMomentumRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const openOperDialog = (event, row) => {
    event.stopPropagation();
    if (toko === "ALL") {
      setNotify({
        isOpen: true,
        type: "error",
        message: "Pilih toko spesifik dulu sebelum melakukan oper.",
        location: "",
      });
      return;
    }
    setSelectedOperRow(row);
    setOperMode(isOutboundStatus(row.status) ? "outbound" : "inbound");
    setOperDialogOpen(true);
  };

  const closeOperDialog = () => {
    setOperDialogOpen(false);
    setSelectedOperRow(null);
  };

  const openDetailDialog = (row) => {
    setSelectedSku(row);
    setDetailDialogOpen(true);
  };

  const closeDetailDialog = () => {
    setDetailDialogOpen(false);
  };

  useEffect(() => {
    getListToko();
  }, []);

  useEffect(() => {
    getMomentumData();
  }, [toko, kategori]);

  useEffect(() => {
    return () => {
      if (skuDebounceRef.current) {
        clearTimeout(skuDebounceRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Typography variant="h4">Momentum Analytics</Typography>
      <Box style={{ height: 24 }} />

      <Box style={{ position: "relative", minHeight: 350 }}>
        {isLoading && (
          <Box
            style={{
              position: "absolute",
              zIndex: 1000,
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.7)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 6,
              gap: 10,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2">Memuat data momentum...</Typography>
          </Box>
        )}

        <Box
          style={{
            display: "flex",
            width: "100%",
            flexWrap: "wrap",
            gap: matchSm ? 20 : 12,
          }}
        >
          <Toolbar
            style={{
              alignItems: "center",
              padding: 0,
              display: "flex",
              width: !matchSm ? "100%" : null,
              justifyContent: !matchSm ? "space-between" : null,
            }}
          >
            <Controls.Label label="Pilih toko:" />
            <Controls.Select
              noNone={true}
              name="toko"
              value={toko}
              onChange={(e) => setToko(e.target.value)}
              otherStyle={{
                marginBottom: 0,
                marginLeft: 10,
              }}
              options={listToko}
            />
          </Toolbar>

          <Toolbar
            style={{
              alignItems: "center",
              padding: 0,
              display: "flex",
              width: !matchSm ? "100%" : null,
              justifyContent: !matchSm ? "space-between" : null,
            }}
          >
            <Controls.Label label="Pilih kategori:" />
            <Controls.Select
              noNone={true}
              name="kategori"
              value={kategori}
              onChange={(e) => setKategori(e.target.value)}
              otherStyle={{
                marginBottom: 0,
                marginLeft: 10,
              }}
              options={categories}
            />
          </Toolbar>

          <Toolbar
            style={{
              alignItems: "center",
              padding: 0,
              display: "flex",
              width: !matchSm ? "100%" : null,
              justifyContent: !matchSm ? "space-between" : null,
            }}
          >
            <Controls.Label label="Filter SKU:" />
            <Controls.Input
              value={skuKeywordInput}
              style={{
                marginBottom: 0,
                marginLeft: 10,
              }}
              onChange={(e) => {
                const inputValue = e.target.value;
                setSkuKeywordInput(inputValue);

                if (skuDebounceRef.current) {
                  clearTimeout(skuDebounceRef.current);
                }

                skuDebounceRef.current = setTimeout(() => {
                  setSkuKeyword(inputValue);
                }, 1000);
              }}
              placeholder="cari SKU"
            />
          </Toolbar>
        </Box>

        <Paper style={{ width: "100%", overflowX: "auto", marginTop: 8 }}>
          <TblContainer>
            <TblHead styles={{ fontSize: 11, padding: 4 }} />
            <TableBody>
              {recordsAfterPagingAndSorting().map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  onClick={() => openDetailDialog(item)}
                >
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {item.SKU || "-"}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {formatNumber(item.global_stock)}
                  </TableCell>
                  {showSelectedStoreStock && (
                    <TableCell style={{ fontSize: 11, padding: 4 }}>
                      {formatNumber(item.selected_store_stock)}
                    </TableCell>
                  )}
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {formatDate(item.first_seen_date)}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {formatNumber(item.SKU_age_days)}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {formatRatio(item.momentum_ratio)}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {item.koleksi || "-"}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {item.status || "-"}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {item.recommendation_action || "-"}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {item.urgency || "-"}
                  </TableCell>
                  <TableCell style={{ fontSize: 11, padding: 4 }}>
                    {(isOperRecommendation(item.recommendation_action) ||
                      isInboundStatus(item.status) ||
                      isOutboundStatus(item.status)) &&
                    toko !== "ALL" ? (
                      <Box
                        style={{ display: "flex", justifyContent: "center" }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Button
                          text="Oper"
                          onClick={(event) => openOperDialog(event, item)}
                        />
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </TblContainer>
          <TblPagination />
        </Paper>
      </Box>

      <OperDialog
        open={operDialogOpen}
        handleClose={closeOperDialog}
        title={
          operMode === "outbound"
            ? `Oper Product ${selectedOperRow?.SKU || ""} DARI ${toko} ke toko lain`
            : `Oper Product ${selectedOperRow?.SKU || ""} Ke ${toko}`
        }
        withoutActions={true}
      >
        {selectedOperRow && operMode === "inbound" ? (
          <SuggestionModalContent
            toko={toko}
            sku={selectedOperRow.SKU}
            segment={segmentByCategory[kategori] || "A"}
            totalSolds={selectedOperRow?.raw?.lifetime_sales || 0}
            // occurences={selectedOperRow?.raw?.occurences || 0}
            purchaseRate={selectedOperRow?.momentum_ratio || 0}
            currentStocks={
              selectedOperRow?.selected_store_stock ??
              selectedOperRow?.global_stock ??
              0
            }
            handleClose={closeOperDialog}
            onConfirmAction={() => {
              closeOperDialog();
              getMomentumData();
            }}
            confirmText="Oper"
          />
        ) : null}
        {selectedOperRow && operMode === "outbound" ? (
          <OutboundSuggestionModalContent
            toko={toko}
            sku={selectedOperRow.SKU}
            segment={segmentByCategory[kategori] || "A"}
            totalSolds={selectedOperRow?.raw?.lifetime_sales || 0}
            // occurences={selectedOperRow?.raw?.occurences || 0}
            currentStocks={
              selectedOperRow?.selected_store_stock ??
              selectedOperRow?.global_stock ??
              0
            }
            handleClose={closeOperDialog}
            onSuccess={() => {
              closeOperDialog();
              getMomentumData();
            }}
          />
        ) : null}
      </OperDialog>

      <OperDialog
        open={detailDialogOpen}
        handleClose={closeDetailDialog}
        title={`Detail SKU ${selectedSku?.SKU || ""}`}
        withoutActions={true}
      >
        <Box>
          <Typography variant="body2" style={{ color: "rgba(0,0,0,0.7)" }}>
            Klik row untuk melihat seluruh field response API per SKU.
          </Typography>
          <Divider style={{ marginTop: 10, marginBottom: 14 }} />

          {selectedSku?.store_distribution?.length ? (
            <Box style={{ marginBottom: 16 }}>
              <Typography variant="subtitle2">Store Distribution</Typography>
              <Paper style={{ marginTop: 8, padding: 10 }}>
                {selectedSku.store_distribution.map((store) => (
                  <Box
                    key={`${selectedSku.SKU}-${store.toko}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 8,
                      padding: "6px 0",
                      borderBottom: "1px solid #efefef",
                    }}
                  >
                    <div>{store.toko}</div>
                    <div>Stock: {formatNumber(store.stock)}</div>
                    <div>Sales 365d: {formatNumber(store.sales_365)}</div>
                  </Box>
                ))}
              </Paper>
            </Box>
          ) : null}

          <Typography variant="subtitle2">Semua Field Response</Typography>
          <Paper style={{ marginTop: 8, padding: 10 }}>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: matchSm ? "220px 1fr" : "1fr",
                gap: 8,
              }}
            >
              {Object.entries(selectedSku?.raw || {}).map(([key, value]) => (
                <Box
                  key={key}
                  style={{
                    display: "contents",
                  }}
                >
                  <Typography style={{ fontWeight: 700 }}>{key}</Typography>
                  <Typography
                    style={{
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                      color: "rgba(0,0,0,0.75)",
                    }}
                  >
                    {renderRawValue(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </OperDialog>

      <Controls.Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
};

const OutboundSuggestionModalContent = ({
  toko,
  sku,
  segment,
  totalSolds,
  currentStocks,
  handleClose,
  occurences,
  onSuccess,
}) => {
  const theme = useTheme();
  const matchSm = useMediaQuery(theme.breakpoints.up("sm"));
  const [availableTargets, setAvailableTargets] = useState([]);
  const [allPossibleSuggestions, setAllPossibleSuggestions] = useState([]);
  const [approvedOper, setApprovedOper] = useState([]);
  const [bufferOper, setBufferOper] = useState([]);
  const [productQty, setProductQty] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isPossibleSugLoading, setPossibleSugLoading] = useState(false);
  const [isApprovedLoading, setApprovedLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    location: "",
  });

  const suggestionHeadCells = [
    { id: "target", label: "Penampung", disableSorting: true },
    { id: "kemunculan", label: "Total Hari Terbeli", disableSorting: true },
    { id: "stok", label: "Stok Toko", disableSorting: true },
    { id: "qty_field", label: "Stok Yang Dioper", disableSorting: true },
    { id: "actions", label: "Actions", disableSorting: true },
  ];

  const bufferHeadCells = [
    { id: "sku", label: "SKU", disableSorting: true },
    { id: "target", label: "Penampung", disableSorting: true },
    { id: "origin_stock", label: "Stok Asal", disableSorting: true },
    { id: "qty", label: "Qty Oper", disableSorting: true },
    { id: "actions", label: "Actions", disableSorting: true },
  ];

  const approvedHeadCells = [
    { id: "sku", label: "SKU", disableSorting: true },
    { id: "origin", label: "Asal", disableSorting: true },
    { id: "target", label: "Penampung", disableSorting: true },
    { id: "origin_stock", label: "Stok Asal", disableSorting: true },
    { id: "qty", label: "Qty", disableSorting: true },
  ];

  const {
    TblHead,
    TblContainer,
    TblPagination,
    styledTableCell: TableCell,
    recordsAfterPagingAndSorting,
  } = useTable(availableTargets, suggestionHeadCells, {
    fn: (items) => items,
  });

  const {
    TblHead: TblHeadAllPS,
    TblContainer: TblContainerAllPS,
    TblPagination: TblPaginationAllPS,
    styledTableCell: TableCellAllPS,
    recordsAfterPagingAndSorting: allPSRecords,
  } = useTable(allPossibleSuggestions, suggestionHeadCells, {
    fn: (items) => items,
  });

  const {
    TblHead: TblHeadBuffer,
    TblContainer: TblContainerBuffer,
    TblPagination: TblPaginationBuffer,
    styledTableCell: TableCellBuffer,
    recordsAfterPagingAndSorting: bufferRecords,
  } = useTable(bufferOper, bufferHeadCells, {
    fn: (items) => items,
  });

  const {
    TblHead: TblHeadApproved,
    TblContainer: TblContainerApproved,
    TblPagination: TblPaginationApproved,
    styledTableCell: TableCellApproved,
    recordsAfterPagingAndSorting: approvedRecords,
  } = useTable(approvedOper, approvedHeadCells, {
    fn: (items) => items,
  });

  const buildProductQty = (items) =>
    (items || []).reduce((acc, item) => {
      acc[item.toko] = 1;
      return acc;
    }, {});

  const getAllPossibleSuggestions = async () => {
    try {
      setPossibleSugLoading(true);
      const { data } = await axios.get(
        `${baseUrl}/oper-stok/suggestions-by-sku/all-possible-options`,
        {
          params: {
            toko,
            sku,
            segment,
          },
        }
      );

      const suggestions = (data?.data?.qualifiedSuggestions || []).filter(
        (item) => item.toko !== toko
      );
      const sorted = suggestions.sort(
        (a, b) => Number(b.kemunculan || 0) - Number(a.kemunculan || 0)
      );
      setAllPossibleSuggestions(sorted);

      if (!availableTargets.length) {
        setAvailableTargets(sorted);
      }

      setProductQty((prev) => ({ ...buildProductQty(sorted), ...prev }));
    } catch (error) {
      console.error(error);
      setAllPossibleSuggestions([]);
    } finally {
      setPossibleSugLoading(false);
    }
  };

  const getSuggestedTargets = async () => {
    try {
      setLoading(true);
      const now = new Date();
      const startDate = formatDatePayload(
        new Date(now.getFullYear(), now.getMonth(), 1)
      );
      const endDate = formatDatePayload(
        new Date(now.getFullYear(), now.getMonth() + 1, 0)
      );
      const { data } = await axios.get(`${baseUrl}/oper-stok/demand-check/target`, {
        params: {
          startDate,
          endDate,
          toko,
          sku,
        },
      });

      const targets = (data?.data || []).filter((item) => item.toko !== toko);
      if (targets.length) {
        setAvailableTargets(targets);
        setProductQty((prev) => ({ ...buildProductQty(targets), ...prev }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getApproved = async () => {
    try {
      setApprovedLoading(true);
      const { data } = await axios.get(`${baseUrl}/oper-stok/records`, {
        params: {
          origin: toko,
          noDate: 1,
          status: "approved",
          search: sku,
        },
      });
      setApprovedOper(data?.data?.movedProduct || []);
    } catch (error) {
      console.error(error);
      setApprovedOper([]);
    } finally {
      setApprovedLoading(false);
    }
  };

  const handleDialog = () => {
    setDialogOpen(!dialogOpen);
  };

  const addStok = (item) => {
    const qty = Number(productQty[item.toko] || 0);
    const maxStokAsal = Number(currentStocks || 0);
    const usedQty = bufferOper.reduce((acc, buf) => acc + Number(buf.qty || 0), 0);
    const nextTotal = usedQty + qty;

    if (!qty || qty < 1) {
      item.error = true;
      setAvailableTargets([...availableTargets]);
      return;
    }

    if (nextTotal > maxStokAsal) {
      setNotify({
        isOpen: true,
        type: "error",
        message: `Total qty oper melebihi stok asal (${maxStokAsal})`,
        location: "",
      });
      return;
    }

    setBufferOper([
      ...bufferOper,
      {
        id: uuidv4(),
        qty,
        origin: toko,
        target: item.toko,
        originStock: Number(currentStocks || 0),
        Klkode: sku,
        ...item,
      },
    ]);

    item.error = false;
  };

  const moveProduct = async () => {
    try {
      if (!dialogOpen || !isSending) return;

      const productArray = bufferOper.map((buf) => ({
        date: formatDatePayload(new Date()),
        origin: buf.origin,
        target: buf.target,
        sku: buf.Klkode,
        qty: Number(buf.qty),
        originStock: Number(buf.originStock),
        approvedAt: new Date(),
      }));

      const { data } = await axios.post(`${baseUrl}/oper-stok/suggestions`, {
        productArray,
        noDate: 1,
      });

      setNotify({
        isOpen: true,
        type: "success",
        message: data?.msg || "Oper berhasil",
        location: "",
      });

      setDialogOpen(false);
      setBufferOper([]);
      setIsSending(false);
      getApproved();
      getSuggestedTargets();
      getAllPossibleSuggestions();
      onSuccess?.();
    } catch (err) {
      setNotify({
        isOpen: true,
        type: "error",
        message: err?.response?.data?.msg || "Gagal melakukan oper",
        location: "",
      });
      setIsSending(false);
    }
  };

  useEffect(() => {
    getSuggestedTargets();
    getAllPossibleSuggestions();
    getApproved();
  }, []);

  useEffect(() => {
    if (isSending) {
      moveProduct();
    }
  }, [isSending]);

  return (
    <Box>
      <Divider style={{ marginBottom: 10 }} />
      <Box
        style={{
          display: "flex",
          flexWrap: matchSm ? "nowrap" : "wrap",
          gap: matchSm ? 20 : null,
        }}
      >
        <Typography
          style={{ flex: `1 1 ${!matchSm ? "100%" : "1 1 calc(100% / 3)"}` }}
          variant="h6"
        >
          SKU: {sku}
        </Typography>
        <Typography
          style={{ flex: `1 1 ${!matchSm ? "100%" : "1 1 calc(100% / 3)"}` }}
          variant="h6"
        >
          Origin: {toko}
        </Typography>
        <Typography
          style={{ flex: `1 1 ${!matchSm ? "100%" : "1 1 calc(100% / 3)"}` }}
          variant="h6"
        >
          Current Stocks: {currentStocks}
        </Typography>
        <Typography
          style={{ flex: `1 1 ${!matchSm ? "100%" : "1 1 calc(100% / 3)"}` }}
          variant="h6"
        >
          Total Solds: {totalSolds}
        </Typography>
        {/* <Typography
          style={{ flex: `1 1 ${!matchSm ? "100%" : "1 1 calc(100% / 3)"}` }}
          variant="h6"
        >
          Occurences: {occurences}
        </Typography> */}
      </Box>
      <Divider style={{ marginTop: 10, marginBottom: 20 }} />

      <Box style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
        <Box style={{ flex: 1, width: "100%" }}>
          <Typography variant="body1">Suggestions (Penampung Utama)</Typography>
          <Box style={{ marginBottom: 10 }} />
          <Paper style={{ position: "relative" }}>
            {isLoading && (
              <Fragment>
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "rgba(0,0,0,0.05)",
                    height: "100%",
                    width: "100%",
                    top: 0,
                    left: 0,
                    borderRadius: 5,
                  }}
                ></div>
                <div
                  style={{
                    position: "absolute",
                    zIndex: 10,
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                  }}
                >
                  <CircularProgress size={50} />
                </div>
              </Fragment>
            )}
            <TblContainer>
              <TblHead />
              <TableBody>
                {recordsAfterPagingAndSorting().map((item, index) => (
                  <TableRow key={index} hover role="checkbox" tabIndex={-1}>
                    <TableCell>{item.toko}</TableCell>
                    <TableCell>{item.kemunculan || "-"}</TableCell>
                    <TableCell>{item.stok || "-"}</TableCell>
                    <TableCell>
                      <TextField
                        error={Boolean(item.error)}
                        defaultValue={1}
                        autoComplete="off"
                        margin="dense"
                        id={`qty-${item.toko}`}
                        type="number"
                        value={productQty[item.toko] || 1}
                        onChange={(e) => {
                          const _productQty = { ...productQty };
                          _productQty[item.toko] = e.target.value;
                          setProductQty(_productQty);
                        }}
                        InputProps={{
                          inputProps: { min: 1, max: Number(currentStocks || 0) },
                        }}
                        fullWidth
                        helperText={`max ${currentStocks || 0}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Box style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          text={"Oper"}
                          onClick={() => {
                            addStok(item);
                            handleDialog();
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TblContainer>
            <TblPagination />
          </Paper>
        </Box>
      </Box>

      <Box style={{ marginBottom: 20 }} />
      <Box style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
        <Box style={{ flex: 1, width: "100%" }}>
          <Typography variant="body1">All Possible Suggestions</Typography>
          <Box style={{ marginBottom: 10 }} />
          <Paper style={{ position: "relative" }}>
            {isPossibleSugLoading && (
              <Fragment>
                <div
                  style={{
                    position: "absolute",
                    backgroundColor: "rgba(0,0,0,0.05)",
                    height: "100%",
                    width: "100%",
                    top: 0,
                    left: 0,
                    borderRadius: 5,
                  }}
                ></div>
                <div
                  style={{
                    position: "absolute",
                    zIndex: 10,
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                  }}
                >
                  <CircularProgress size={50} />
                </div>
              </Fragment>
            )}
            <TblContainerAllPS>
              <TblHeadAllPS />
              <TableBody>
                {allPSRecords().map((item, index) => (
                  <TableRow key={index} hover role="checkbox" tabIndex={-1}>
                    <TableCellAllPS>{item.toko}</TableCellAllPS>
                    <TableCellAllPS>{item.kemunculan || "-"}</TableCellAllPS>
                    <TableCellAllPS>{item.stok || "-"}</TableCellAllPS>
                    <TableCellAllPS>
                      <TextField
                        error={Boolean(item.error)}
                        defaultValue={1}
                        autoComplete="off"
                        margin="dense"
                        id={`qty-all-${item.toko}`}
                        type="number"
                        value={productQty[item.toko] || 1}
                        onChange={(e) => {
                          const _productQty = { ...productQty };
                          _productQty[item.toko] = e.target.value;
                          setProductQty(_productQty);
                        }}
                        InputProps={{
                          inputProps: { min: 1, max: Number(currentStocks || 0) },
                        }}
                        fullWidth
                        helperText={`max ${currentStocks || 0}`}
                      />
                    </TableCellAllPS>
                    <TableCellAllPS>
                      <Box style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          text={"Oper"}
                          onClick={() => {
                            addStok(item);
                            handleDialog();
                          }}
                        />
                      </Box>
                    </TableCellAllPS>
                  </TableRow>
                ))}
              </TableBody>
            </TblContainerAllPS>
            <TblPaginationAllPS />
          </Paper>
        </Box>
      </Box>

      <Box style={{ marginBottom: 20 }} />
      <Box style={{ flex: 1, width: "100%" }}>
        <Typography variant="body1">Buffer Oper (Origin: {toko})</Typography>
        <Box style={{ marginBottom: 10 }} />
        <Paper>
          <TblContainerBuffer>
            <TblHeadBuffer />
            <TableBody>
              {bufferRecords().map((item, index) => (
                <TableRow key={index} hover role="checkbox" tabIndex={-1}>
                  <TableCellBuffer>{item.Klkode}</TableCellBuffer>
                  <TableCellBuffer>{item.target}</TableCellBuffer>
                  <TableCellBuffer>{item.originStock}</TableCellBuffer>
                  <TableCellBuffer>{item.qty}</TableCellBuffer>
                  <TableCellBuffer>
                    <Box style={{ display: "flex", justifyContent: "center" }}>
                      <Button
                        text={"Delete"}
                        onClick={() => {
                          const _buffer = [...bufferOper];
                          _buffer.splice(
                            _buffer.findIndex((buf) => buf.id === item.id),
                            1
                          );
                          setBufferOper(_buffer);
                        }}
                      />
                    </Box>
                  </TableCellBuffer>
                </TableRow>
              ))}
            </TableBody>
          </TblContainerBuffer>
          <TblPaginationBuffer />
          <DialogActions>
            <Button onClick={handleClose} text="Cancel" />
            <Button
              text="Oper Semua"
              disabled={!bufferOper.length}
              onClick={handleDialog}
            />
          </DialogActions>
        </Paper>
      </Box>

      <Box style={{ marginBottom: 20 }} />
      <Box style={{ flex: 1, width: "100%" }}>
        <Typography variant="body1">Approved</Typography>
        <Box style={{ marginBottom: 10 }} />
        <Paper style={{ position: "relative" }}>
          {isApprovedLoading && (
            <Fragment>
              <div
                style={{
                  position: "absolute",
                  backgroundColor: "rgba(0,0,0,0.05)",
                  height: "100%",
                  width: "100%",
                  top: 0,
                  left: 0,
                  borderRadius: 5,
                }}
              ></div>
              <div
                style={{
                  position: "absolute",
                  zIndex: 10,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                }}
              >
                <CircularProgress size={50} />
              </div>
            </Fragment>
          )}
          <TblContainerApproved>
            <TblHeadApproved />
            <TableBody>
              {approvedRecords().map((item, index) => (
                <TableRow key={index} hover role="checkbox" tabIndex={-1}>
                  <TableCellApproved>{item.sku}</TableCellApproved>
                  <TableCellApproved>{item.origin}</TableCellApproved>
                  <TableCellApproved>{item.target}</TableCellApproved>
                  <TableCellApproved>{item.origin_stock}</TableCellApproved>
                  <TableCellApproved>{item.qty}</TableCellApproved>
                </TableRow>
              ))}
            </TableBody>
          </TblContainerApproved>
          <TblPaginationApproved />
        </Paper>
      </Box>

      <OperDialog
        open={dialogOpen}
        title={`Apakah anda ingin mengoper produk dari ${toko} ?`}
        confirmText="Confirm"
        disableBtn={isSending}
        handleClose={handleDialog}
        onConfirmAction={() => {
          setIsSending(true);
        }}
      />
      <Controls.Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
};

export default MomentumAnalytics;
